/**
 * Inventory + refill API client — talks to mttr/app_api.
 *
 * Both endpoints are scoped server-side to the caller's visible SCUs; the
 * token is sent via the `access-token` header (gen5-pwa convention).
 */

import { ApiError, apiFetch } from "./apiClient";

export interface InventoryItem {
  item_id: number;
  sku: string | null;
  name: string | null;
  on_hand: number;
  min_qty: number | null;
  suggest_qty: number | null;
  refill_needed: boolean;
  refill_qty: number;
}

export interface SmartboxInventory {
  scu_name: string;
  smartbox_id: number | null;
  /** Operator-facing SmartBox identifier (md_smartbox.code, e.g.
   *  "SB001"). The SmartBox is the long-lived object; the SCU is
   *  the device-instance and can change after a Pi swap. */
  smartbox_code: string | null;
  workcenter_id: number | null;
  work_center: string | null;
  customer: string | null;
  scan_id: number | null;
  scan_end_utc: string | null;
  total_tags: number | null;
  matched_tag_count: number;
  unmatched_count: number;
  unmatched: string[];
  items: InventoryItem[];
}

export interface RefillLine {
  scu_name: string;
  smartbox_code: string | null;
  workcenter_name: string | null;
  item_id: number;
  sku: string | null;
  name: string | null;
  on_hand: number;
  min_qty: number;
  suggest_qty: number | null;
  refill_qty: number;
}

export interface RefillBySku {
  sku: string | null;
  name: string | null;
  total_refill_qty: number;
  smartbox_count: number;
}

export interface RefillReport {
  total_lines: number;
  lines: RefillLine[];
  by_sku: RefillBySku[];
}

export class InventoryApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "InventoryApiError";
  }
}

async function getJson<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new InventoryApiError(e.message, e.status) : e;
  }
}

export function fetchInventory(
  scu: string,
  token: string,
): Promise<SmartboxInventory> {
  return getJson<SmartboxInventory>(
    `/api/boxes/${encodeURIComponent(scu)}/inventory`,
    token,
  );
}

/** One row returned by /api/boxes/{scu}/needs-attention — an on-hand
 *  EPC whose reliability bucket is FLICKERY or UNRELIABLE. sku/name are
 *  null when the EPC has no md_rfid mapping. */
export interface NeedsAttentionTag {
  epc: string;
  bucket: "FLICKERY" | "UNRELIABLE";
  total_scans: number;
  read_scans: number;
  /** read_scans / total_scans; null if total_scans == 0. */
  read_ratio: number | null;
  longest_miss: number;
  current_miss_streak: number;
  last_updated_utc: string | null;
  item_id: number | null;
  sku: string | null;
  item_name: string | null;
}

export interface SmartboxNeedsAttention {
  scu: string;
  count: number;
  tags: NeedsAttentionTag[];
}

export function fetchNeedsAttention(
  scu: string,
  token: string,
): Promise<SmartboxNeedsAttention> {
  return getJson<SmartboxNeedsAttention>(
    `/api/boxes/${encodeURIComponent(scu)}/needs-attention`,
    token,
  );
}

/** Drill-down: the RFID tags (EPCs) behind one item's on-hand count in a box.
 *  Excludes manually-checked-out tags (matches the on-hand number). */
export function fetchItemEpcs(
  scu: string,
  itemId: number,
  token: string,
): Promise<{ scu_name: string; item_id: number; epcs: string[] }> {
  return getJson(
    `/api/boxes/${encodeURIComponent(scu)}/inventory/${itemId}/epcs`,
    token,
  );
}

export function fetchRefill(
  token: string,
  workcenter?: string,
): Promise<RefillReport> {
  const q = workcenter ? `?workcenter=${encodeURIComponent(workcenter)}` : "";
  return getJson<RefillReport>(`/api/refill${q}`, token);
}

/** Set the MTTR-side min/max override for one item at a smartbox's
 *  workcenter (platform + vendor-admin only, enforced server-side). */
export async function setMinmax(
  scu: string,
  itemId: number,
  minQty: number | null,
  suggestQty: number | null,
  token: string,
): Promise<{ min_qty: number | null; suggest_qty: number | null }> {
  return getJson<{ min_qty: number | null; suggest_qty: number | null }>(
    `/api/boxes/${encodeURIComponent(scu)}/items/${itemId}/minmax`,
    token,
    {
      method: "POST",
      body: JSON.stringify({ min_qty: minQty, suggest_qty: suggestQty }),
    },
  );
}


/** One (item, smartbox) row returned by /api/inventory/locate — the "where
 *  does this part exist across the fleet?" search. on_hand is by definition
 *  > 0 (zero-on-hand combinations are filtered server-side). */
export interface InventoryLocationRow {
  item_id: number;
  sku: string | null;
  name: string | null;
  scu_name: string;
  smartbox_code: string | null;
  workcenter_name: string | null;
  customer_name: string | null;
  on_hand: number;
}

export interface InventoryLocateResult {
  query: string;
  total: number;
  locations: InventoryLocationRow[];
}

/** Cross-fleet part search. Returns every smartbox where any item whose SKU
 *  or name matches `query` currently has on-hand > 0. Scoped server-side to
 *  the requester's visible SCUs. Empty query returns no results. */
export function searchInventoryLocations(
  query: string,
  token: string,
): Promise<InventoryLocateResult> {
  return getJson<InventoryLocateResult>(
    `/api/inventory/locate?q=${encodeURIComponent(query)}`,
    token,
  );
}
